import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, Wrench, Thermometer, ShieldCheck, Phone, Droplets, CheckCircle2, ArrowRight, ArrowLeft, Calendar, X } from 'lucide-react';
import { getServiceBySlug, services } from '../data/services';
const iconMap = {
  Flame,
  Wrench,
  Thermometer,
  ShieldCheck,
  Phone,
  Droplets
};
export function ServiceDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const service = slug ? getServiceBySlug(slug) : undefined;
  const [lightbox, setLightbox] = useState<string | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!service) {
    return <Navigate to="/" replace data-id="element-287" />;
  }
  const Icon = iconMap[service.iconName];
  const otherServices = services.filter(s => s.slug !== service.slug).slice(0, 3);
  return <div className="pt-28 pb-20 bg-[#0a0a0a]" data-id="element-288">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 mb-8" data-id="element-289">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#FFD600] transition-colors text-sm" data-id="element-290">
          <ArrowLeft className="w-4 h-4" data-id="element-291" />
          Back to Home
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6" data-id="element-292">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center" data-id="element-293">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-294">
            <div className="inline-flex items-center gap-3 mb-6" data-id="element-295">
              <div className="w-12 h-12 rounded-full bg-gradient-primary p-[1px]" data-id="element-296">
                <div className="w-full h-full bg-[#0a0a0a] rounded-full flex items-center justify-center" data-id="element-297">
                  <Icon className="w-5 h-5 text-[#FF9500]" data-id="element-298" />
                </div>
              </div>
              <span className="text-[#FFD600] font-bold tracking-widest uppercase text-xs" data-id="element-299">
                Our Service
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6" data-id="element-300">
              {service.title}
            </h1>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-301">
              {service.longDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-302">
              <a href="tel:07845242803" className="inline-flex items-center justify-center gap-2 bg-gradient-primary text-black font-bold px-6 py-3 rounded-full hover:shadow-[0_0_30px_rgba(255,149,0,0.3)] transition-all" data-id="element-303">
                <Phone className="w-5 h-5" data-id="element-304" />
                Call 07845 242803
              </a>
              <Link to="/#contact" className="inline-flex items-center justify-center gap-2 border-2 border-[#FFD600] text-[#FFD600] font-bold px-6 py-3 rounded-full hover:bg-[#FFD600]/10 transition-all" data-id="element-305">
                <Calendar className="w-5 h-5" data-id="element-306" />
                Get a Free Quote
              </Link>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="relative" data-id="element-307">
            <div className="absolute inset-0 bg-gradient-primary rounded-3xl blur-2xl opacity-20 transform rotate-3" data-id="element-308"></div>
            <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-brand-card aspect-[4/5] md:aspect-[5/6]" data-id="element-309">
              <img src={service.heroImage} alt={service.title} className="w-full h-full object-cover" data-id="element-310" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-311"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 mt-24" data-id="element-312">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10" data-id="element-313">
          <div className="lg:col-span-1" data-id="element-314">
            <span className="text-[#FFD600] font-bold tracking-widest uppercase text-xs mb-3 block" data-id="element-315">
              What's Included
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white leading-tight" data-id="element-316">
              Done right, the first time.
            </h2>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-317">
            {service.features.map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="flex items-start gap-3 bg-brand-card border border-white/5 rounded-xl p-5" data-id="element-318">
                <CheckCircle2 className="w-5 h-5 text-[#FFD600] flex-shrink-0 mt-0.5" data-id="element-319" />
                <span className="text-white font-medium" data-id="element-320">{feature}</span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-6 mt-24" data-id="element-321">
        <div className="mb-10" data-id="element-322">
          <span className="text-[#FFD600] font-bold tracking-widest uppercase text-xs mb-3 block" data-id="element-323">
            Recent Work
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-white" data-id="element-324">
            From Our {service.title} Jobs
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-325">
          {service.gallery.map((src, i) => <motion.button key={i} type="button" onClick={() => setLightbox(src)} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="group relative aspect-square rounded-2xl overflow-hidden border border-white/5 bg-brand-card" aria-label={`Open ${service.title} photo ${i + 1}`} data-id="element-326">
              <img src={src} alt={`${service.title} job ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-327" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-328"></div>
            </motion.button>)}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 mt-24" data-id="element-329">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-brand-card p-10 md:p-16 text-center" data-id="element-330">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-radial from-[#FF9500]/10 to-transparent rounded-full blur-3xl pointer-events-none" data-id="element-331"></div>
          <div className="relative" data-id="element-332">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4" data-id="element-333">
              Ready to get started?
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto mb-8" data-id="element-334">
              Get a free, no-obligation quote for your{' '}
              {service.title.toLowerCase()} from a local Gas Safe registered
              engineer.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-335">
              <a href="tel:07845242803" className="inline-flex items-center justify-center gap-2 bg-gradient-primary text-black font-bold px-8 py-4 rounded-full hover:shadow-[0_0_30px_rgba(255,149,0,0.3)] transition-all" data-id="element-336">
                <Phone className="w-5 h-5" data-id="element-337" />
                Call Now
              </a>
              <Link to="/#contact" className="inline-flex items-center justify-center gap-2 border-2 border-[#FFD600] text-[#FFD600] font-bold px-8 py-4 rounded-full hover:bg-[#FFD600]/10 transition-all" data-id="element-338">
                Get a Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="max-w-7xl mx-auto px-6 mt-24" data-id="element-339">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4" data-id="element-340">
          <div data-id="element-341">
            <span className="text-[#FFD600] font-bold tracking-widest uppercase text-xs mb-3 block" data-id="element-342">
              Explore More
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white" data-id="element-343">
              Other Services
            </h2>
          </div>
          <Link to="/#services" className="inline-flex items-center gap-2 text-[#FFD600] font-semibold hover:text-white transition-colors" data-id="element-344">
            View all <ArrowRight className="w-4 h-4" data-id="element-345" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-346">
          {otherServices.map(s => {
          const OtherIcon = iconMap[s.iconName];
          return <Link key={s.slug} to={`/services/${s.slug}`} className="group relative bg-brand-card rounded-2xl overflow-hidden border border-white/5 hover:border-white/10 transition-all duration-300 hover:-translate-y-1" data-id="element-347">
                <div className="aspect-video overflow-hidden" data-id="element-348">
                  <img src={s.heroImage} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-349" />
                </div>
                <div className="p-6" data-id="element-350">
                  <div className="w-12 h-12 rounded-full bg-gradient-primary p-[1px] mb-4" data-id="element-351">
                    <div className="w-full h-full bg-brand-card rounded-full flex items-center justify-center" data-id="element-352">
                      <OtherIcon className="w-5 h-5 text-[#FF9500]" data-id="element-353" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-354">
                    {s.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 leading-relaxed" data-id="element-355">
                    {s.shortDescription}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#FFD600] font-semibold text-sm" data-id="element-356">
                    Learn more <ArrowRight className="w-4 h-4" data-id="element-357" />
                  </span>
                </div>
              </Link>;
        })}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)} role="dialog" aria-modal="true" data-id="element-358">
          <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" aria-label="Close" data-id="element-359">
            <X className="w-5 h-5" data-id="element-360" />
          </button>
          <img src={lightbox} alt="Enlarged view" className="max-h-[90vh] max-w-[90vw] object-contain rounded-2xl" onClick={e => e.stopPropagation()} data-id="element-361" />
        </div>}
    </div>;
}